import React from 'react';

import {
	Box,
	Button,
	Divider,
	Link,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
	Chip,
} from '@mui/material';
import { useServiceTypeMenu } from '../AddServiceTypeMenu';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import GroupIcon from '@mui/icons-material/Group';
import CircleIcon from '@mui/icons-material/Circle';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
// import BadgeIcon from '@mui/icons-material/Badge';
import { IServices, serviceTypes, IPassenger, TypeServiceEnum } from 'features/salesOrder/types';
import { useHistory } from 'react-router-dom';
import { DoNotDisturb } from '@mui/icons-material';

// Helper functions
const truncate = (str: string, maxLength: number) => {
	return str.length > maxLength ? str.substring(0, maxLength - 3) + '...' : str;
};

// @ts-ignore
const getServiceDescription = (service) => {
	if (!service || !service.serviceType) {
		return truncate('No especificado', 40);
	}
	let description;
	switch (service.serviceType) {
		case TypeServiceEnum.Accommodation: {
			const destinationCode = service.destination?.code ?? 'N/A';
			const serviceProviderName = service.serviceProvider?.name ?? 'N/A';
			const roomsLength = service.rooms?.length ?? 0;
			description = `${destinationCode}: ${serviceProviderName} | ${roomsLength} hab`;
			break;
		}
		case TypeServiceEnum.Flight: {
			const vcCode = service.vc?.code ?? 'N/A';
			const reference = service.reference ?? 'N/A';
			const passengerIdsLength = getPassengerIds(service).length;
			description = `vc: ${vcCode} | pnr: ${reference} | ${passengerIdsLength} pax`;
			break;
		}
		case TypeServiceEnum.FlightAncillary: {
			const flightAncillaryType = service.flightAncillaryType ?? 'N/A';
			const vcCode = service.vc?.code ?? 'N/A';
			const reference = service.reference ?? 'N/A';
			const passengerIdsLength = getPassengerIds(service).length;
			description = `${flightAncillaryType} | vc: ${vcCode} | pnr: ${reference} | ${passengerIdsLength} pax`;
			break;
		}
		case TypeServiceEnum.Other: {
			const otherServiceType = service.otherServiceType ?? 'Otro';
			const serviceName = service.serviceName ?? 'N/A';
			const passengerIdsLength = getPassengerIds(service).length;
			description = `${otherServiceType}: ${serviceName} | ${passengerIdsLength} pax`;
			break;
		}
		case TypeServiceEnum.Transfer: {
			const transferSegment = service.transferSegments?.[0];
			const transferType = transferSegment?.transferType ?? 'N/A';
			const originName = transferSegment?.originName ?? 'N/A';
			const destinationName = transferSegment?.destinationName ?? 'N/A';
			const passengerIdsLength = getPassengerIds(service).length;
			description = `${transferType} ${originName} ${destinationName} | ${passengerIdsLength} pax`;
			break;
		}
		case TypeServiceEnum.Fee: {
			const feeServiceType = service.feeServiceType ?? 'Fee';
			const passengerIdsLength = getPassengerIds(service).length;
			description = `${feeServiceType} | ${passengerIdsLength} pax`;
			break;
		}
		default: {
			description = service.serviceType ?? 'No especificado';
			break;
		}
	}
	return truncate(description, 40);
};

const getPassengerIds = (service: any): string[] => {
	// @ts-ignore
	switch (service.serviceType) {
		case TypeServiceEnum.Accommodation: {
			// Para alojamiento, los pasajeros están en las habitaciones
			const allPassengerIds: string[] = [];
			if (service.rooms && Array.isArray(service.rooms)) {
				service.rooms.forEach((room: any) => {
					if (room.passengerIds && Array.isArray(room.passengerIds)) {
						allPassengerIds.push(...room.passengerIds);
					}
				});
			}
			return Array.from(new Set(allPassengerIds)); // Eliminar duplicados
		}
		case TypeServiceEnum.Flight: {
			// Para vuelos, los pasajeros están en passengersGroupByPassengerType
			const allPassengerIds: string[] = [];
			if (service.passengersGroupByPassengerType && Array.isArray(service.passengersGroupByPassengerType)) {
				service.passengersGroupByPassengerType.forEach((group: any) => {
					if (group.passengerIds && Array.isArray(group.passengerIds)) {
						allPassengerIds.push(...group.passengerIds);
					}
				});
			}
			return Array.from(new Set(allPassengerIds)); // Eliminar duplicados
		}
		case TypeServiceEnum.FlightAncillary: {
			// Para FlightAncillary, usar passengerIds directamente
			return service.passengerIds || [];
		}
		default: {
			// Para otros tipos de servicios, usar passengerIds directamente
			return service.passengerIds || [];
		}
	}
};

const getPassengerNames = (service: any, passengers: IPassenger[]): string[] => {
	const passengerIds = getPassengerIds(service);
	return passengerIds
		.map((id) => {
			const passenger = passengers.find((p) => p.keyId === id);
			return passenger ? `${passenger.name} ${passenger.lastName}` : null;
		})
		.filter(Boolean) as string[];
};

// Función para verificar el estado de los tickets en servicios de vuelo
const getTicketStatus = (service: any): 'green' | 'orange' | null => {
	// Solo aplicar a servicios de vuelo y ancillary
	if (service.serviceType !== TypeServiceEnum.Flight && service.serviceType !== TypeServiceEnum.FlightAncillary) {
		return null;
	}

	let allTickets: any[] = [];

	if (service.serviceType === TypeServiceEnum.Flight) {
		// Para vuelos, obtener tickets de todos los grupos de pasajeros
		if (service.passengersGroupByPassengerType && Array.isArray(service.passengersGroupByPassengerType)) {
			service.passengersGroupByPassengerType.forEach((group: any) => {
				if (group.passengersTickets && Array.isArray(group.passengersTickets)) {
					allTickets.push(...group.passengersTickets);
				}
			});
		}
	} else if (service.serviceType === TypeServiceEnum.FlightAncillary) {
		// Para FlightAncillary, usar passengersTickets directamente
		if (service.passengersTickets && Array.isArray(service.passengersTickets)) {
			allTickets = service.passengersTickets;
		}
	}

	// Si no hay tickets, retornar null (no mostrar indicador)
	if (allTickets.length === 0) {
		return null;
	}

	// Verificar si todos los tickets son diferentes de "-"
	const allTicketsValid = allTickets.every((ticket) => ticket.ticket && ticket.ticket.trim() !== '-');

	return allTicketsValid ? 'green' : 'orange';
};

// Función para verificar el estado de referencia en servicios de transfer
const getTransferReferenceStatus = (service: any): 'green' | 'orange' | null => {
	// Solo aplicar a servicios de transfer
	if (service.serviceType !== TypeServiceEnum.Transfer) {
		return null;
	}

	// Verificar si existe reference y no es "-" ni está vacío
	if (!service.reference || service.reference.trim() === '' || service.reference.trim() === '-') {
		return 'orange'; // Pendiente
	}

	return 'green'; // Confirmado
};

interface Props {
	services: IServices[];
	passengers: IPassenger[];
	selectedItemIndex: number | string;
	onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number | string) => void;
	onAddNewService: (serviceType: TypeServiceEnum) => void;
}

export const ListMenu = ({ selectedItemIndex, services, passengers, onClick, onAddNewService }: Props): JSX.Element => {
	const history = useHistory();
	const { handleOpen, menu, open, menuId } = useServiceTypeMenu({ onSelect: onAddNewService });

	return (
		<Box sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}>
			<Link
				sx={{ cursor: 'pointer' }}
				variant="body1"
				color="secondary"
				onClick={() => history.push('/salesorder/list')}
			>
				volver al listado
			</Link>
			<List component="nav" aria-label="salesorder leftmenu">
				<ListItemButton selected={selectedItemIndex === 0} onClick={(event) => onClick(event, 0)}>
					<ListItemIcon>
						<QueryStatsIcon />
					</ListItemIcon>
					<ListItemText primary="General" />
				</ListItemButton>
				<ListItemButton selected={selectedItemIndex === 1} onClick={(event) => onClick(event, 1)}>
					<ListItemIcon>
						<GroupIcon />
					</ListItemIcon>
					<ListItemText primary="Pasajeros" />
				</ListItemButton>
				<ListItemButton selected={selectedItemIndex === 2} onClick={(event) => onClick(event, 2)}>
					<ListItemIcon>
						<PaymentsIcon />
					</ListItemIcon>
					<ListItemText primary="Pagos Proveedores" />
				</ListItemButton>
				<ListItemButton selected={selectedItemIndex === 3} onClick={(event) => onClick(event, 3)}>
					<ListItemIcon>
						<ReceiptLongIcon />
					</ListItemIcon>
					<ListItemText primary="Recibos y Facturas" />
				</ListItemButton>
			</List>
			<Divider />
			<List component="nav" aria-label="salesorder leftmenu">
				{services.map((service) => {
					const { serviceType, keyId } = service;
					const serviceDetail = serviceTypes.find((st) => st.id === serviceType);
					const serviceDescription = getServiceDescription(service);
					const passengerNames = getPassengerNames(service, passengers);
					const ticketStatus = getTicketStatus(service);
					const transferStatus = getTransferReferenceStatus(service);
					const serviceStatus = ticketStatus || transferStatus;

					return (
						<ListItem key={keyId} sx={{ flexDirection: 'column', alignItems: 'stretch', mb: 1 }}>
							<ListItemButton
								selected={selectedItemIndex === keyId}
								onClick={(event) => onClick(event, service.keyId)}
								sx={{
									flexDirection: 'column',
									alignItems: 'flex-start',
									py: 1.5,
									'&.Mui-selected': {
										backgroundColor: 'primary.light',
										color: 'primary.contrastText',
										'& .MuiTypography-root': {
											color: 'inherit',
										},
									},
								}}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}>
									<ListItemIcon sx={{ minWidth: 36 }}>{serviceDetail?.icon}</ListItemIcon>
									<Box sx={{ flexGrow: 1 }}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<Typography variant="subtitle2" fontWeight="bold">
												{serviceDetail?.name}
											</Typography>
											{serviceStatus && (
												<CircleIcon
													sx={{
														color: serviceStatus === 'green' ? '#4caf50' : '#ff9800',
														fontSize: 12,
													}}
												/>
											)}
											{service.enable === false && <DoNotDisturb sx={{ color: 'red', fontSize: 18 }} />}
										</Box>
										<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
											{serviceDescription}
										</Typography>
									</Box>
								</Box>

								{passengerNames.length > 0 && (
									<Box sx={{ width: '100%', mt: 1, ml: 4.5 }}>
										<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
											{passengerNames.map((name, index) => (
												<Chip
													key={index}
													label={name}
													size="small"
													variant="outlined"
													sx={{
														fontSize: '0.7rem',
														height: 20,
														backgroundColor: 'white',
														borderColor: 'grey.300',
														color: 'text.primary',
														'& .MuiChip-label': {
															px: 0.5,
															color: 'text.primary',
														},
													}}
												/>
											))}
										</Box>
									</Box>
								)}
							</ListItemButton>
						</ListItem>
					);
				})}
			</List>
			<Button
				id="add-service-button"
				variant="contained"
				color="primary"
				onClick={handleOpen}
				fullWidth
				aria-controls={open ? menuId : undefined}
				aria-haspopup="true"
				aria-expanded={open ? 'true' : undefined}
				sx={{
					'&:focus-visible': {
						outline: '3px solid',
						outlineColor: 'primary.main',
						outlineOffset: 2,
					},
				}}
			>
				Agregar Servicio
			</Button>
			{menu}
		</Box>
	);
};
